import { create } from 'zustand';
import { cartAPI } from '../api/services';

const GUEST_KEY = 'seoul-guest-cart';

const loadGuest = () => {
  try { return JSON.parse(localStorage.getItem(GUEST_KEY) || '[]'); } catch { return []; }
};
const saveGuest = (items) => {
  try { localStorage.setItem(GUEST_KEY, JSON.stringify(items)); } catch {}
};
const isLoggedIn = () => !!localStorage.getItem('token');

export const useCartStore = create((set, get) => ({
  items: isLoggedIn() ? [] : loadGuest(),
  loading: false,
  error: null,
  drawerOpen: false,
  openDrawer: () => set({ drawerOpen: true }),
  closeDrawer: () => set({ drawerOpen: false }),

  fetchCart: async () => {
    if (!isLoggedIn()) {
      set({ items: loadGuest() });
      return;
    }
    set({ loading: true });
    try {
      const response = await cartAPI.getCart();
      set({ items: response.data, loading: false });
    } catch {
      set({ loading: false });
    }
  },

  addToCart: async (productId, quantity, product = null) => {
    if (!isLoggedIn()) {
      const items = loadGuest();
      const existing = items.find(i => i.product_id === productId);
      const newItems = existing
        ? items.map(i => i.product_id === productId ? { ...i, quantity: i.quantity + quantity } : i)
        : [...items, { id: Date.now(), product_id: productId, quantity, product }];
      saveGuest(newItems);
      set({ items: newItems, drawerOpen: true });
      return;
    }
    set({ loading: true, error: null });
    try {
      const response = await cartAPI.addToCart({ product_id: productId, quantity });
      const items = get().items;
      const existing = items.find(i => i.product_id === productId);
      set({
        items: existing
          ? items.map(i => i.product_id === productId ? { ...i, quantity: i.quantity + quantity } : i)
          : [...items, response.data],
        loading: false,
        drawerOpen: true,
      });
    } catch (error) {
      set({ loading: false, error: 'Failed to add to cart' });
      throw error;
    }
  },

  updateCartItem: async (itemId, quantity) => {
    if (!isLoggedIn()) {
      const items = loadGuest().map(i => i.id === itemId ? { ...i, quantity } : i);
      saveGuest(items);
      set({ items });
      return;
    }
    set({ loading: true, error: null });
    try {
      await cartAPI.updateCartItem(itemId, quantity);
      set({
        items: get().items.map(i => i.id === itemId ? { ...i, quantity } : i),
        loading: false,
      });
    } catch (error) {
      set({ loading: false, error: 'Failed to update cart' });
      throw error;
    }
  },

  removeFromCart: async (itemId) => {
    if (!isLoggedIn()) {
      const items = loadGuest().filter(i => i.id !== itemId);
      saveGuest(items);
      set({ items });
      return;
    }
    set({ loading: true, error: null });
    try {
      await cartAPI.removeFromCart(itemId);
      set({ items: get().items.filter(i => i.id !== itemId), loading: false });
    } catch (error) {
      set({ loading: false, error: 'Failed to remove item' });
      throw error;
    }
  },

  clearCart: async () => {
    if (!isLoggedIn()) {
      saveGuest([]);
      set({ items: [] });
      return;
    }
    set({ loading: true, error: null });
    try {
      await cartAPI.clearCart();
      set({ items: [], loading: false });
    } catch (error) {
      set({ loading: false, error: 'Failed to clear cart' });
      throw error;
    }
  },

  mergeGuestCart: async () => {
    const guestItems = loadGuest();
    if (!guestItems.length) return;
    try {
      for (const item of guestItems) {
        await cartAPI.addToCart({ product_id: item.product_id, quantity: item.quantity });
      }
      saveGuest([]);
    } catch {}
  },

  getCartTotal: () =>
    get().items.reduce((total, item) => {
      const price = (item.product?.discount_price && item.product?.discount_active !== false ? item.product.discount_price : null) || item.product?.price || 0;
      return total + price * item.quantity;
    }, 0),

  getCartItemsCount: () =>
    get().items.reduce((count, item) => count + item.quantity, 0),
}));
